import { SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Pencil, FileText, MessageSquare, Save, X, Trash2, ChevronsRight, Send } from 'lucide-react';
import { getTimeAgo } from '../../utils/claimHelpers';

/**
 * Top region of EditClaimPanel:
 *  • sticky close button + "unsaved changes" warning bubble
 *  • title block (report no, DOT recordable, time-ago, deleted banner)
 *  • read-mode tab bar (Update / Docs / Notes / Send)
 *  • edit-mode header (Save / Cancel / Delete)
 */
export default function ClaimHeader({
  claim, lossType, editMode, saving, deleteIsPartial,
  showCloseWarning, setShowCloseWarning,
  onClose, onSave, onCancel, onRequestDelete,
  activeTab, setActiveTab,
  setEditMode, setDocsOpen, setSendUpdateOpen,
  setNotesLossType, setNotesOpen,
}) {
  const handleTabClick = (id) => {
    if (id === 'update') {
      setEditMode(true);
      return;
    }
    if (id === 'docs') { setDocsOpen(true); return; }
    if (id === 'send') { setSendUpdateOpen(true); return; }
    if (id === 'notes') {
      if (lossType) {
        setNotesLossType(lossType);
        setNotesOpen(true);
      } else {
        const types = [
          { key: 'al', active: claim?.al },
          { key: 'pd', active: claim?.pd },
          { key: 'cd', active: claim?.cd },
          { key: 'ocac', active: claim?.ocac },
          { key: 'wc', active: claim?.wc },
        ].filter(t => t.active);
        if (types.length === 1) {
          setNotesLossType(types[0].key);
          setNotesOpen(true);
        } else if (types.length > 1) {
          setActiveTab('notes-picker');
        }
      }
      return;
    }
    setActiveTab(id);
  };

  return (
    <>
      {/* Sticky close button + unsaved-changes warning */}
      <div className="sticky top-0 z-20 flex justify-end pointer-events-none">
        <div className="pointer-events-auto relative">
          <button onClick={onClose} className="m-2 p-1.5 text-blue-500 hover:text-blue-700 transition-colors rounded-full hover:bg-blue-50" data-testid="edit-panel-close">
            <ChevronsRight className="w-7 h-7" />
          </button>
          {showCloseWarning && (
            <div className="absolute right-0 top-12 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-30" data-testid="close-warning-dialog">
              <p className="text-sm text-gray-700 mb-3">You have unsaved changes.</p>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setShowCloseWarning(false)} className="text-xs h-7">Cancel</Button>
                <Button size="sm" variant="outline" onClick={() => { setShowCloseWarning(false); onSave(); }} className="text-xs h-7 text-blue-600 border-blue-300 hover:bg-blue-50">
                  <Save className="w-3.5 h-3.5 mr-1" />Save
                </Button>
                <Button size="sm" variant="destructive" onClick={() => { setShowCloseWarning(false); setEditMode(false); onClose(true); }} className="text-xs h-7">Discard</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Title */}
      <div className="px-4 pt-0 pb-1">
        <SheetTitle className="text-xl font-bold text-blue-600 text-center" data-testid="edit-panel-title">
          {claim.report_no}
        </SheetTitle>
        {claim.dot_recordable && (
          <p className="text-red-600 font-bold text-sm text-center" data-testid="dot-recordable-badge">DOT Recordable</p>
        )}
        <p className="text-xs text-blue-500 italic mt-0.5" data-testid="edit-updated-ago">
          {getTimeAgo(
            lossType ? (claim.type_data?.[lossType]?.updated_at || claim.updated_at) : claim.updated_at,
            lossType ? (claim.type_data?.[lossType]?.updated_by_name || claim.updated_by_name) : claim.updated_by_name
          )}
        </p>
        {claim.deleted === true && (
          <div className="mt-2 mx-auto max-w-md flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700" data-testid="claim-deleted-banner">
            <span className="text-base leading-none">⚠️</span>
            <div className="text-xs">
              <p className="font-semibold">This claim is in the Recycle Bin</p>
              <p className="text-red-600/90 mt-0.5">
                Deleted {claim.deleted_at ? new Date(claim.deleted_at).toLocaleDateString() : ''}{claim.deleted_by_name ? ` by ${claim.deleted_by_name}` : ''}. Restore it from Recycle Bin to edit. Saves will be rejected.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Read-only mode: Tab bar */}
      {!editMode && (
        <div className="flex items-center justify-center gap-8 border-b px-4 py-2">
          {[
            { id: 'update', icon: Pencil,         label: 'Update' },
            { id: 'docs',   icon: FileText,       label: 'Docs' },
            { id: 'notes',  icon: MessageSquare,  label: 'Notes' },
            { id: 'send',   icon: Send,           label: 'Send' },
          ].map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => handleTabClick(id)}
              className={`flex items-center gap-1.5 px-2 py-1 text-sm font-medium transition-colors ${activeTab === id ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`edit-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Edit-mode header */}
      {editMode && (
        <div className="border-b px-4 py-3">
          <p className="text-center text-lg font-bold text-gray-900 mb-3" data-testid="edit-mode-header">Edit | Update</p>
          <div className="flex items-center justify-center gap-6">
            <button onClick={onSave} disabled={saving} className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors" data-testid="edit-mode-save-top">
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={onCancel} className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors" data-testid="edit-mode-cancel-top">
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button onClick={onRequestDelete} className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-red-600 transition-colors" data-testid="edit-mode-delete-top">
              <Trash2 className="w-4 h-4" />
              {deleteIsPartial ? `Remove ${lossType?.toUpperCase()}` : 'Delete'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
